import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Ruler,
  Activity,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { getAccessToken } from '../Auth/AuthUtil';

const BASE_URL = 'https://nczldagsfg.execute-api.ap-south-1.amazonaws.com/dev';

// const BASE_URL = 'http://localhost:8000'

export default function AddUser() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [patientUid, setPatientUid] = useState('');

  // Form State - Step 1
  const [username, setUsername] = useState('');
  const [contact, setContact] = useState(''); // Number or Email ID

  // Form State - Step 2
  const [gender, setGender] = useState('');
  const [yob, setYob] = useState('');
  const [height, setHeight] = useState('');
  const [heightUnit, setHeightUnit] = useState('cm');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');

  const handleUnitChange = (newUnit) => {
    setHeightUnit(newUnit);
    if (newUnit === 'ft') {
      const cmVal = parseFloat(height);
      if (cmVal && !isNaN(cmVal)) {
        const totalInches = cmVal / 2.54;
        const ft = Math.floor(totalInches / 12);
        const inch = Math.round(totalInches % 12);
        setHeightFeet(ft.toString());
        setHeightInches(inch.toString());
      }
    } else {
      const ftVal = parseFloat(heightFeet) || 0;
      const inVal = parseFloat(heightInches) || 0;
      if (ftVal > 0 || inVal > 0) {
        const calculatedCm = Math.round((ftVal * 30.48) + (inVal * 2.54));
        setHeight(calculatedCm.toString());
      }
    }
  };

  const [weight, setWeight] = useState('');
  const [waistCircumference, setWaistCircumference] = useState('');
  const [waistUnit, setWaistUnit] = useState('cm');
  const [waistInches, setWaistInches] = useState('');

  const handleWaistUnitChange = (newUnit) => {
    setWaistUnit(newUnit);
    if (newUnit === 'inches') {
      const cmVal = parseFloat(waistCircumference);
      if (cmVal && !isNaN(cmVal)) {
        setWaistInches(Math.round(cmVal / 2.54).toString());
      }
    } else {
      const inVal = parseFloat(waistInches);
      if (inVal && !isNaN(inVal)) {
        setWaistCircumference(Math.round(inVal * 2.54).toString());
      }
    }
  };

  // Form State - Step 3
  const [isDiabetic, setIsDiabetic] = useState('');
  const [diabetesType, setDiabetesType] = useState('');
  const [fastingGlucose, setFastingGlucose] = useState('');
  const [hba1c, setHba1c] = useState('');

  // Generate Year of Birth options (from 1920 to current year)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 1920; y--) {
    years.push(y);
  }

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!contact.trim()) {
      newErrors.contact = 'Mobile number or Email ID is required';
    } else {
      const isEmail = contact.includes('@');
      if (isEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contact.trim())) {
          newErrors.contact = 'Please enter a valid email address';
        }
      } else {
        // Assume phone number, strip +91 if present and check if it's 10 digits
        const cleanPhone = contact.trim().replace(/^\+91/, '').replace(/\s+/g, '');
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(cleanPhone)) {
          newErrors.contact = 'Mobile number must be exactly 10 digits';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!yob) {
      newErrors.yob = 'Year of birth is required';
    }
    // Height, Weight, Waist are optional — only validate if a value is entered
    if (height) {
      const hVal = parseFloat(height);
      if (isNaN(hVal) || hVal <= 0 || hVal > 300) {
        newErrors.height = 'Please enter a valid height';
      }
    }
    if (weight) {
      const wtVal = parseFloat(weight);
      if (isNaN(wtVal) || wtVal <= 0 || wtVal > 500) {
        newErrors.weight = 'Please enter a valid weight';
      }
    }
    if (waistCircumference) {
      const wVal = parseFloat(waistCircumference);
      if (isNaN(wVal) || wVal <= 0 || wVal > 250) {
        newErrors.waistCircumference = 'Please enter a valid waist circumference';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!isDiabetic) {
      newErrors.isDiabetic = 'Diabetic status selection is required';
    } else if (isDiabetic === 'Yes') {
      if (!diabetesType) {
        newErrors.diabetesType = 'Please select a diabetes type';
      }
      if (fastingGlucose) {
        const fg = parseFloat(fastingGlucose);
        if (isNaN(fg) || fg <= 0) {
          newErrors.fastingGlucose = 'Fasting Glucose must be a valid number';
        }
      }
      if (hba1c) {
        const hb = parseFloat(hba1c);
        if (isNaN(hb) || hb <= 0 || hb > 25) {
          newErrors.hba1c = 'HbA1c must be a valid percentage';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Step 1
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;

    setLoading(true);
    try {
      const isEmail = contact.includes('@');
      const cleanContact = contact.trim();

      // Prepare payload to match potential backend options
      const payload = {
        username: username.trim(),
        UID: cleanContact,
        name: username.trim(),
        email: isEmail ? cleanContact.toLowerCase() : '',
        phone: !isEmail ? (cleanContact.startsWith('+91') ? cleanContact : `+91${cleanContact}`) : '',
        mobile: !isEmail ? (cleanContact.startsWith('+91') ? cleanContact : `+91${cleanContact}`) : '',
        contact: cleanContact
      };

      // Set headers for auth
      const config = {
        headers: {}
      };
      const token = getAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Hit checkKioskUser endpoint
      const res = await axios.post(`${BASE_URL}/api/v1/checkClinicalKioskUser`, payload, config);

      if (res.status === 200 || res.status === 201) {
        const data = res.data;
        // Capture UID or ID resiliently
        const uid = data.UID || data.uid || data.id || data._id || data.patient?.UID || data.patient?.id ||
          (data.data && (data.data.UID || data.data.uid || data.data.id)) ||
          (data.user && (data.user.UID || data.user.uid || data.user.id || data.user._id));

        if (uid) {
          setPatientUid(uid);
          toast.success('Step 1 successful! Please complete physical profile.');
        } else {
          // If no UID is returned, generate a fallback based on contact to proceed
          console.warn('No direct UID found in checkKioskUser response:', data);
          const fallbackUid = cleanContact.replace(/[^a-zA-Z0-9]/g, '');
          setPatientUid(fallbackUid);
        }
        setStep(2);
        setErrors({});
      } else {
        toast.error('Failed to verify user. Please try again.');
      }
    } catch (err) {
      console.error('Error in step 1 submit:', err);
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Something went wrong while verifying user.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Step 2
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);
    try {
      const isEmail = contact.includes('@');
      const emailVal = isEmail ? contact.trim().toLowerCase() : '';
      const phoneVal = !isEmail ? contact.trim() : '';

      // Extract firstname and lastname from username
      const names = username.trim().split(/\s+/);
      const firstnameVal = names[0] || '';
      const lastnameVal = names.slice(1).join(' ') || '';

      // Get kiosk details for payload
      let kiosk_details = {};
      const kioskCookie = Cookies.get('kiosk_details');
      if (kioskCookie) {
        try {
          kiosk_details = JSON.parse(kioskCookie);
        } catch (err) {
          console.error('Error parsing kiosk details cookie in AddUser:', err);
        }
      }

      // Build final update/profile details payload as UserResponse DTO
      const payload = {
        UID: patientUid || contact.trim(),
        email: emailVal,
        phone: phoneVal,
        firstname: firstnameVal,
        lastname: lastnameVal,
        source: 'Out Patient',
        gender: gender,
        dob: `0101${yob}`, // formatted to "0101YYYY"
        height: height ? (heightUnit === 'ft' ? `${heightFeet || 0}.${heightInches || 0}ft` : `${height} cm`) : "",
        weight: weight ? `${weight} kg` : "",
        src: "",
        kiosk_id: kiosk_details?.kiosk_id || ""
      };

      const config = {
        headers: {}
      };
      const token = getAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Hit Profile endpoint
      const res = await axios.post(`${BASE_URL}/ClinicalProfile`, payload, config);

      if (res.status === 200 || res.status === 201) {
        toast.success('Basic profile details saved!');
        setStep(3); // Go to Step 3: Diabetes Check
      } else {
        toast.error('Failed to update profile. Please check inputs.');
      }
    } catch (err) {
      console.error('Error in step 2 submit:', err);
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to submit physical details.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Step 3
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const payload = {
        userId: patientUid || contact.trim(),
        isDiabetic: isDiabetic === 'Yes'
      };

      if (isDiabetic === 'Yes') {
        payload.diabetesType = diabetesType;
        if (fastingGlucose) payload.fastingGlucose = parseFloat(fastingGlucose);
        if (hba1c) payload.hba1c = parseFloat(hba1c);
      }

      const config = {
        headers: {}
      };
      const token = getAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Hit followup endpoint
      const res = await axios.post(`${BASE_URL}/api/v1/clinical/diabetes-assessment/followup`, payload, config);

      if (res.status === 200 || res.status === 201) {
        toast.success('Diabetes profile details updated!');
        setStep(4); // Success page view
      } else {
        toast.error('Failed to submit diabetes details. Please try again.');
      }
    } catch (err) {
      console.error('Error in step 3 submit:', err);
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to submit diabetes details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#F8F8F4' }}>

      {/* Header & Back Action */}
      <Box sx={{ width: '100%', maxWidth: '650px', display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} sx={{ color: 'var(--themePrimaryColor, #DA6E0B)', mr: 1 }}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B362A', fontFamily: 'Poppins' }}>
          Back to Home
        </Typography>
      </Box>

      {/* Stepper Progress bar */}
      {step < 4 && (
        <Box sx={{ width: '100%', maxWidth: '650px', mb: 4, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: step === 1 ? 'var(--themePrimaryColor, #DA6E0B)' : '#918165', fontFamily: 'Poppins', fontSize: '0.8rem' }}>
              Step 1: Check patient
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: step === 2 ? 'var(--themePrimaryColor, #DA6E0B)' : '#918165', fontFamily: 'Poppins', fontSize: '0.8rem' }}>
              Step 2: Profile Info
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: step === 3 ? 'var(--themePrimaryColor, #DA6E0B)' : '#918165', fontFamily: 'Poppins', fontSize: '0.8rem' }}>
              Step 3: Diabetes Check
            </Typography>
          </Box>
          <Box sx={{ height: '6px', width: '100%', backgroundColor: '#E7E7DB', borderRadius: '3px', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: step === 1 ? '33.3%' : step === 2 ? '66.6%' : '100%',
                backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
                transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          </Box>
        </Box>
      )}

      {/* Main Registration Card */}
      <Card sx={{ width: '100%', maxWidth: '650px', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.03)', border: '1px solid rgba(218,110,11,0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          {step === 1 && (
            <form onSubmit={handleStep1Submit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B362A', mb: 1, letterSpacing: '-0.5px', fontFamily: 'Poppins' }}>
                  Add Patient
                </Typography>
                <Typography variant="body2" sx={{ color: '#75623F', fontFamily: 'Poppins' }}>
                  Please enter the username and contact details to check or register.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="patient"
                    placeholder="Enter patient"
                    variant="outlined"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setErrors(prev => ({ ...prev, username: null })); }}
                    error={!!errors.username}
                    helperText={errors.username}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <User size={18} color="#918165" />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mobile Number or Email ID"
                    placeholder="Enter 10-digit number or email"
                    variant="outlined"
                    value={contact}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        if (val.length <= 10) {
                          setContact(val);
                        }
                      } else {
                        setContact(val);
                      }
                      setErrors(prev => ({ ...prev, contact: null }));
                    }}
                    error={!!errors.contact || (contact && /^\d+$/.test(contact) && contact.length < 10)}
                    helperText={errors.contact || (contact && /^\d+$/.test(contact) && contact.length < 10 ? 'Mobile number must be exactly 10 digits' : '')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {contact.includes('@') ? (
                            <Mail size={18} color="#918165" />
                          ) : (
                            <Phone size={18} color="#918165" />
                          )}
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                    '&:hover': { bgcolor: '#B85C09' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    py: 1.8,
                    fontWeight: 700,
                    fontSize: '1rem',
                    fontFamily: 'Poppins',
                    boxShadow: '0 8px 16px rgba(218, 110, 11, 0.15)'
                  }}
                >
                  {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Next'}
                </Button>
              </Box>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleStep2Submit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B362A', mb: 1, letterSpacing: '-0.5px', fontFamily: 'Poppins' }}>
                  Health info
                </Typography>
                {/* <Typography variant="body2" sx={{ color: '#75623F', fontFamily: 'Poppins' }}>
                  Complete the profile for user: <strong>{username}</strong> (UID: {patientUid})
                </Typography> */}
              </Box>

              <Grid container spacing={3}>
                {/* Gender visual selectors */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.gender}>
                    <InputLabel id="gender-select-label" sx={{ '&.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' } }}>Gender</InputLabel>
                    <Select
                      labelId="gender-select-label"
                      value={gender}
                      label="Gender"
                      onChange={(e) => { setGender(e.target.value); setErrors(prev => ({ ...prev, gender: null })); }}
                      sx={{
                        borderRadius: '12px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                      }}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {errors.gender && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.gender}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Year of birth dropdown selector */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.yob}>
                    <InputLabel id="yob-select-label" sx={{ '&.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' } }}>Year of Birth</InputLabel>
                    <Select
                      labelId="yob-select-label"
                      value={yob}
                      label="Year of Birth"
                      onChange={(e) => { setYob(e.target.value); setErrors(prev => ({ ...prev, yob: null })); }}
                      sx={{
                        borderRadius: '12px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                      }}
                    >
                      {years.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                    {errors.yob && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.yob}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Height Field with Unit Selector */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B362A', fontFamily: 'Poppins' }}>
                        Height Unit <Typography component="span" variant="caption" sx={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: '#E7E7DB', p: 0.5, borderRadius: '8px' }}>
                        <Button
                          size="small"
                          onClick={() => handleUnitChange('cm')}
                          sx={{
                            minWidth: '45px',
                            py: 0.2,
                            px: 1.5,
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            fontSize: '0.75rem',
                            backgroundColor: heightUnit === 'cm' ? 'var(--themePrimaryColor, #DA6E0B)' : 'transparent',
                            color: heightUnit === 'cm' ? 'white !important' : '#75623F',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: heightUnit === 'cm' ? 'var(--themePrimaryColor, #DA6E0B)' : 'rgba(0,0,0,0.05)'
                            }
                          }}
                        >
                          cm
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleUnitChange('ft')}
                          sx={{
                            minWidth: '45px',
                            py: 0.2,
                            px: 1.5,
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            fontSize: '0.75rem',
                            backgroundColor: heightUnit === 'ft' ? 'var(--themePrimaryColor, #DA6E0B)' : 'transparent',
                            color: heightUnit === 'ft' ? 'white !important' : '#75623F',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: heightUnit === 'ft' ? 'var(--themePrimaryColor, #DA6E0B)' : 'rgba(0,0,0,0.05)'
                            }
                          }}
                        >
                          ft
                        </Button>
                      </Box>
                    </Box>

                    {heightUnit === 'cm' ? (
                      <TextField
                        fullWidth
                        label="Height"
                        placeholder="175"
                        variant="outlined"
                        value={height}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setHeight(val);
                          setErrors(prev => ({ ...prev, height: null }));
                        }}
                        error={!!errors.height}
                        helperText={errors.height}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Ruler size={18} color="#918165" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                          fullWidth
                          label="Feet"
                          placeholder="5"
                          variant="outlined"
                          value={heightFeet}
                          onChange={(e) => {
                            const newFeet = e.target.value.replace(/[^0-9]/g, '');
                            setHeightFeet(newFeet);
                            const ftVal = parseFloat(newFeet) || 0;
                            const inVal = parseFloat(heightInches) || 0;
                            if (ftVal > 0 || inVal > 0) {
                              const calculatedCm = Math.round((ftVal * 30.48) + (inVal * 2.54));
                              setHeight(calculatedCm.toString());
                            } else {
                              setHeight('');
                            }
                            setErrors(prev => ({ ...prev, height: null }));
                          }}
                          error={!!errors.height}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Inches"
                          placeholder="7"
                          variant="outlined"
                          value={heightInches}
                          onChange={(e) => {
                            const cleanInput = e.target.value.replace(/[^0-9]/g, '');
                            if (cleanInput === '') {
                              setHeightInches('');
                              const ftVal = parseFloat(heightFeet) || 0;
                              if (ftVal > 0) {
                                setHeight(Math.round(ftVal * 30.48).toString());
                              } else {
                                setHeight('');
                              }
                              setErrors(prev => ({ ...prev, height: null }));
                              return;
                            }

                            const inchesVal = parseInt(cleanInput, 10);
                            if (inchesVal >= 12) {
                              const additionalFeet = Math.floor(inchesVal / 12);
                              const remainingInches = inchesVal % 12;
                              const currentFeet = parseInt(heightFeet, 10) || 0;
                              const newFeet = currentFeet + additionalFeet;

                              setHeightFeet(newFeet.toString());
                              setHeightInches(remainingInches.toString());

                              const calculatedCm = Math.round((newFeet * 30.48) + (remainingInches * 2.54));
                              setHeight(calculatedCm.toString());
                            } else {
                              setHeightInches(cleanInput);
                              const ftVal = parseFloat(heightFeet) || 0;
                              const calculatedCm = Math.round((ftVal * 30.48) + (inchesVal * 2.54));
                              setHeight(calculatedCm.toString());
                            }
                            setErrors(prev => ({ ...prev, height: null }));
                          }}
                          error={!!errors.height}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                          }}
                        />
                      </Box>
                    )}
                    {heightUnit === 'ft' && errors.height && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.height}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Weight Field (Optional) */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B362A', fontFamily: 'Poppins', mb: 0.5 }}>
                      Weight <Typography component="span" variant="caption" sx={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</Typography>
                    </Typography>
                    <TextField
                      fullWidth
                      label="Weight"
                      placeholder="70"
                      variant="outlined"
                      value={weight}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setWeight(val);
                        setErrors(prev => ({ ...prev, weight: null }));
                      }}
                      error={!!errors.weight}
                      helperText={errors.weight}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Activity size={18} color="#918165" />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end">kg</InputAdornment>
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                      }}
                    />
                  </Box>
                </Grid>

                {/* Waist Circumference Field with Unit Selector */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#3B362A', fontFamily: 'Poppins' }}>
                        Waist Unit <Typography component="span" variant="caption" sx={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: '#E7E7DB', p: 0.5, borderRadius: '8px' }}>
                        <Button
                          size="small"
                          onClick={() => handleWaistUnitChange('cm')}
                          sx={{
                            minWidth: '45px',
                            py: 0.2,
                            px: 1.5,
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            fontSize: '0.75rem',
                            backgroundColor: waistUnit === 'cm' ? 'var(--themePrimaryColor, #DA6E0B)' : 'transparent',
                            color: waistUnit === 'cm' ? 'white !important' : '#75623F',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: waistUnit === 'cm' ? 'var(--themePrimaryColor, #DA6E0B)' : 'rgba(0,0,0,0.05)'
                            }
                          }}
                        >
                          cm
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleWaistUnitChange('inches')}
                          sx={{
                            minWidth: '45px',
                            py: 0.2,
                            px: 1.5,
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            fontSize: '0.75rem',
                            backgroundColor: waistUnit === 'inches' ? 'var(--themePrimaryColor, #DA6E0B)' : 'transparent',
                            color: waistUnit === 'inches' ? 'white !important' : '#75623F',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: waistUnit === 'inches' ? 'var(--themePrimaryColor, #DA6E0B)' : 'rgba(0,0,0,0.05)'
                            }
                          }}
                        >
                          inches
                        </Button>
                      </Box>
                    </Box>

                    {waistUnit === 'cm' ? (
                      <TextField
                        fullWidth
                        label="Waist Circumference"
                        placeholder="90"
                        variant="outlined"
                        value={waistCircumference}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setWaistCircumference(val);
                          setErrors(prev => ({ ...prev, waistCircumference: null }));
                        }}
                        error={!!errors.waistCircumference}
                        helperText={errors.waistCircumference}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Activity size={18} color="#918165" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">cm</InputAdornment>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Waist Circumference"
                        placeholder="36"
                        variant="outlined"
                        value={waistInches}
                        onChange={(e) => {
                          const cleanInput = e.target.value.replace(/[^0-9]/g, '');
                          setWaistInches(cleanInput);
                          const parsedInches = parseFloat(cleanInput) || 0;
                          if (parsedInches > 0) {
                            const calculatedCm = Math.round(parsedInches * 2.54);
                            setWaistCircumference(calculatedCm.toString());
                          } else {
                            setWaistCircumference('');
                          }
                          setErrors(prev => ({ ...prev, waistCircumference: null }));
                        }}
                        error={!!errors.waistCircumference}
                        helperText={errors.waistCircumference}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Activity size={18} color="#918165" />
                            </InputAdornment>
                          ),
                          endAdornment: <InputAdornment position="end">inches</InputAdornment>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                        }}
                      />
                    )}
                  </Box>
                </Grid>

              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setStep(1)}
                  disabled={loading}
                  sx={{
                    borderColor: 'var(--themePrimaryColor, #DA6E0B)',
                    color: 'var(--themePrimaryColor, #DA6E0B)',
                    '&:hover': { borderColor: '#B85C09', backgroundColor: 'var(--themeSecondaryColor, #FFF4E5)' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    py: 1.8,
                    fontWeight: 700,
                    fontFamily: 'Poppins'
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                    '&:hover': { bgcolor: '#B85C09' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    py: 1.8,
                    fontWeight: 700,
                    fontFamily: 'Poppins',
                    boxShadow: '0 8px 16px rgba(218, 110, 11, 0.15)'
                  }}
                >
                  {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Submit'}
                </Button>
              </Box>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleStep3Submit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B362A', mb: 1, letterSpacing: '-0.5px', fontFamily: 'Poppins' }}>
                  Diabetes Check
                </Typography>
                <Typography variant="body2" sx={{ color: '#75623F', fontFamily: 'Poppins' }}>
                  Provide diabetes assessment details for patient: <strong>{username}</strong>.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.isDiabetic}>
                    <InputLabel id="diabetic-select-label" sx={{ '&.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' } }}>Are you diabetic?</InputLabel>
                    <Select
                      labelId="diabetic-select-label"
                      value={isDiabetic}
                      label="Are you diabetic?"
                      onChange={(e) => {
                        setIsDiabetic(e.target.value);
                        setErrors(prev => ({ ...prev, isDiabetic: null }));
                        if (e.target.value === 'No') {
                          setDiabetesType('');
                          setFastingGlucose('');
                          setHba1c('');
                        }
                      }}
                      sx={{
                        borderRadius: '12px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                      }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                    {errors.isDiabetic && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.isDiabetic}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Collapse in={isDiabetic === 'Yes'} sx={{ width: '100%' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.diabetesType}>
                          <InputLabel id="diabetes-type-select-label" sx={{ '&.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' } }}>Diabetes Type</InputLabel>
                          <Select
                            labelId="diabetes-type-select-label"
                            value={diabetesType}
                            label="Diabetes Type"
                            onChange={(e) => { setDiabetesType(e.target.value); setErrors(prev => ({ ...prev, diabetesType: null })); }}
                            sx={{
                              borderRadius: '12px',
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                            }}
                          >
                            <MenuItem value="Type 1">Type 1</MenuItem>
                            <MenuItem value="Type 2">Type 2</MenuItem>
                          </Select>
                          {errors.diabetesType && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors.diabetesType}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Fasting Glucose"
                          placeholder="110"
                          variant="outlined"
                          value={fastingGlucose}
                          onChange={(e) => { setFastingGlucose(e.target.value); setErrors(prev => ({ ...prev, fastingGlucose: null })); }}
                          error={!!errors.fastingGlucose}
                          helperText={errors.fastingGlucose}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">mg/dL</InputAdornment>
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="HbA1c"
                          placeholder="6.0"
                          variant="outlined"
                          value={hba1c}
                          onChange={(e) => { setHba1c(e.target.value); setErrors(prev => ({ ...prev, hba1c: null })); }}
                          error={!!errors.hba1c}
                          helperText={errors.hba1c}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' }
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setStep(2)}
                  disabled={loading}
                  sx={{
                    borderColor: 'var(--themePrimaryColor, #DA6E0B)',
                    color: 'var(--themePrimaryColor, #DA6E0B)',
                    '&:hover': { borderColor: '#B85C09', backgroundColor: 'var(--themeSecondaryColor, #FFF4E5)' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    py: 1.8,
                    fontWeight: 700,
                    fontFamily: 'Poppins'
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                    '&:hover': { bgcolor: '#B85C09' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    py: 1.8,
                    fontWeight: 700,
                    fontFamily: 'Poppins',
                    boxShadow: '0 8px 16px rgba(218, 110, 11, 0.15)'
                  }}
                >
                  {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Submit'}
                </Button>
              </Box>
            </form>
          )}

          {step === 4 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle2 size={80} color="#1C8686" style={{ margin: '0 auto 20px' }} />
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#3B362A', mb: 2, fontFamily: 'Poppins' }}>
                User Registered Successfully!
              </Typography>
              <Typography variant="body1" sx={{ color: '#75623F', mb: 4, fontFamily: 'Poppins' }}>
                The profile and assessment details for <strong>{username}</strong> have been saved.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate(`/Patient/${patientUid}`)}
                sx={{
                  bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                  '&:hover': { bgcolor: '#B85C09' },
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 4,
                  py: 1.8,
                  fontWeight: 700,
                  fontFamily: 'Poppins'
                }}
              >
                Go to Profile
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
